import { Upload01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { useFilePicker } from '@/lib/base/file-upload';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import { useFileAssociation } from '@/lib/crm/hooks/useFileAssociation';
import { RecordAttachmentRow } from '@/lib/crm/record/attachments/RecordAttachmentRow';
import { useRecordAttachments } from '@/lib/crm/record/attachments/useRecordAttachments';

import { CollapsibleSection } from './collapsible-section';
import { SectionActionButton } from './section-action-button';

interface FilesSectionProps {
  orgId: number;
  entityId: string;
  entityTypeName: string;
}

export function FilesSection({
  orgId,
  entityId,
  entityTypeName,
}: FilesSectionProps) {
  const { hasPermission } = usePermissions();
  const canAttachFile = hasPermission('ahoy_file', 'ahoy_object_created');
  const canRemove = hasPermission(entityTypeName, 'ahoy_association_removed');

  const { association, isLoading } = useFileAssociation(orgId, entityTypeName);

  // Read the record from the store to get its associations (kept live by WS).
  const storeObject = useCrmObjectSnapshot(entityId, orgId);

  const {
    fileEntityIds,
    isUploading,
    isSubmitting,
    uploadError,
    detachError,
    handleFile,
    clearUploadError,
    handleRemove,
    handleDownload,
  } = useRecordAttachments(storeObject, orgId, entityTypeName);

  const { filePickerInput, openFilePicker } = useFilePicker({
    onFileSelected: handleFile,
  });

  const openAttachPicker = useCallback(() => {
    clearUploadError();
    openFilePicker();
  }, [clearUploadError, openFilePicker]);

  const actionButton = useMemo(
    () =>
      canAttachFile ? (
        <SectionActionButton
          label={isUploading ? 'Uploading…' : 'Attach file'}
          onClick={openAttachPicker}
          icon={Upload01}
          disabled={isUploading}
        />
      ) : undefined,
    [canAttachFile, isUploading, openAttachPicker]
  );

  if (isLoading || !association) {
    return null;
  }

  return (
    <CollapsibleSection title='Files' actions={actionButton}>
      {filePickerInput}
      {uploadError && (
        <p className='text-xs md:text-sm text-[var(--color-text-error-primary)]'>
          {uploadError}
        </p>
      )}
      {detachError && (
        <p className='text-xs md:text-sm text-[var(--color-text-error-primary)]'>
          {detachError}
        </p>
      )}
      {fileEntityIds.length === 0 ? (
        <p className='text-xs md:text-sm text-fg-secondary'>No files yet</p>
      ) : (
        <div className='flex flex-col gap-2'>
          {fileEntityIds.map(fileId => (
            <RecordAttachmentRow
              key={fileId}
              entityId={fileId}
              orgId={orgId}
              onDownload={handleDownload}
              onRemove={
                canRemove && !isSubmitting && !isUploading
                  ? handleRemove
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}
